import dayjs from "dayjs";
import { get, isEmpty, isEqual, pickBy } from "lodash";
import { SOURCE_STATUSES } from "utils/constants/sourceStatus";

export { SOURCE_STATUSES };

export const SOURCE_STATUS_VARIANTS = {
  [SOURCE_STATUSES.UPCOMING]: "info",
  [SOURCE_STATUSES.ACTIVE]: "green",
  [SOURCE_STATUSES.ENDED]: "warning",
  [SOURCE_STATUSES.STOPPED]: "default",
};

const toDate = (value) => (value ? dayjs(value) : null);

export const toSourceFormValues = (source = {}) => ({
  name: get(source, "name") || "",
  platform: get(source, "platform") || "",
  process: { id: get(source, "process.id") || "" },
  startDate: toDate(get(source, "startDate")),
  endDate: toDate(get(source, "endDate")),
  fixed: get(source, "fixed") === true,
  leadTypologyMapping: get(source, "leadTypologyMapping") || {},
});

const isSameDay = (left, right) => {
  if (!left || !right) return !left && !right;
  return dayjs(left).isSame(dayjs(right), "day");
};

export const sourceEndedByDate = (source, now = dayjs()) => {
  const endDate = get(source, "endDate");
  if (!endDate) return false;
  return dayjs(endDate).isBefore(now);
};

/** A source without a start date is started from its creation. */
export const sourceHasStarted = (source, now = dayjs()) => {
  const startDate = get(source, "startDate");
  if (!startDate) return true;
  return !dayjs(startDate).isAfter(now, "day");
};

const startOfDay = (value) =>
  value ? dayjs(value).startOf("day").toISOString() : null;

const endOfDay = (value) =>
  value ? dayjs(value).endOf("day").toISOString() : null;

/**
 * A source covers whole days: it opens at the start of its first day and
 * closes at the end of its last one. On update only the changed fields are
 * sent, so an unchanged day keeps its stored time; the start never changes.
 */
export const toSourcePayload = (values, { initial } = {}) => {
  const name = String(get(values, "name") || "").trim();
  const platform = String(get(values, "platform") || "").trim();
  const processId = get(values, "process.id");
  const fixed = get(values, "fixed") === true;
  const payload = {
    name,
    platform,
    fixed,
    process: { id: processId },
    startDate: startOfDay(values.startDate),
    endDate: endOfDay(values.endDate),
    leadTypologyMapping: get(values, "leadTypologyMapping") || {},
  };
  if (isEmpty(initial)) return payload;
  return pickBy(payload, (_, field) => {
    if (field === "startDate") return false;
    if (field === "name") return name !== get(initial, "name");
    if (field === "platform") return platform !== get(initial, "platform");
    if (field === "fixed") return fixed !== get(initial, "fixed");
    if (field === "process") return processId !== get(initial, "process.id");
    if (field === "leadTypologyMapping") {
      return !isEqual(
        get(values, "leadTypologyMapping") || {},
        get(initial, "leadTypologyMapping") || {},
      );
    }
    return !isSameDay(values[field], initial[field]);
  });
};
